/**
 * Date formatting utilities — all display in IST (Asia/Kolkata).
 * Database stores UTC; these functions convert for display only.
 */

const IST = 'Asia/Kolkata'

/**
 * Ensure a date string is treated as UTC.
 * SQLite strips timezone info, so naive ISO strings (no Z or +00:00)
 * must be suffixed with Z so JavaScript parses them as UTC.
 */
function asUTC(d: string | Date): Date {
  if (d instanceof Date) return d
  const s = String(d)
  // If it already has timezone info, parse as-is
  if (s.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(s)) return new Date(s)
  // Naive ISO string from SQLite — treat as UTC
  return new Date(s + 'Z')
}

const IST_OFFSET = '+05:30' // India has no DST, so the offset is fixed

/**
 * Value for an `<input type="datetime-local">`, in IST: "2026-03-28T15:45".
 * Use for defaults and for pre-filling from a stored (UTC) timestamp —
 * `new Date().toISOString().slice(0, 16)` gives UTC and shows 5½ hours early.
 */
export function toISTInputValue(d: string | Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: IST,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(asUTC(d as string))
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '00'
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`
}

/**
 * Convert an IST `datetime-local` value ("2026-03-28T15:45") to a UTC ISO
 * string for the API ("2026-03-28T10:15:00.000Z"), whatever the browser's
 * own timezone. Returns null for an empty value.
 */
export function istInputToUTC(value: string): string | null {
  if (!value) return null
  const d = new Date(`${value.length === 16 ? `${value}:00` : value}${IST_OFFSET}`)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

/** Full date+time: "28 Mar 2026, 3:45 PM" */
export function fmtDateTime(d: string | Date | null | undefined): string {
  if (!d) return '—'
  return asUTC(d as string).toLocaleString('en-IN', {
    timeZone: IST,
    day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  })
}

/** Date only: "28 Mar 2026" */
export function fmtDate(d: string | Date | null | undefined): string {
  if (!d) return '—'
  return asUTC(d as string).toLocaleDateString('en-IN', {
    timeZone: IST,
    day: 'numeric', month: 'short', year: 'numeric',
  })
}

/** Short date: "28 Mar" */
export function fmtDateShort(d: string | Date | null | undefined): string {
  if (!d) return '—'
  return asUTC(d as string).toLocaleDateString('en-IN', {
    timeZone: IST,
    day: 'numeric', month: 'short',
  })
}

/** Time only: "3:45 PM" */
export function fmtTime(d: string | Date | null | undefined): string {
  if (!d) return '—'
  return asUTC(d as string).toLocaleTimeString('en-IN', {
    timeZone: IST,
    hour: 'numeric', minute: '2-digit', hour12: true,
  })
}

/** Relative time: "2 hours ago", "3 days ago" */
export function fmtRelative(d: string | Date | null | undefined): string {
  if (!d) return '—'
  const diff = Date.now() - asUTC(d as string).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  return `${months}mo ago`
}

/** Weekday short: "Mon", "Tue" — for heatmaps */
export function fmtWeekday(d: string | Date): string {
  return asUTC(d as string).toLocaleDateString('en-IN', {
    timeZone: IST,
    weekday: 'short',
  })
}

/** Day+month short for heatmaps: "28 Mar" */
export function fmtDayMonth(d: string | Date): string {
  return asUTC(d as string).toLocaleDateString('en-IN', {
    timeZone: IST,
    day: 'numeric', month: 'short',
  })
}
